import { useState, useEffect } from 'react'
import { Copy, Check, QrCode as QrCodeIcon, Clock } from 'lucide-react'
import QRCode from 'qrcode'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { useToast } from '@/components/ui/use-toast'
import type { PixPayment as PixPaymentType } from '@/types/payment'
import { formatCurrency } from '@/lib/utils'

interface PixPaymentProps {
  pixData: PixPaymentType
  onPaymentConfirmed?: () => void
}

export function PixPayment({ pixData }: PixPaymentProps) {
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string>('')
  const [copied, setCopied] = useState(false)
  const [timeLeft, setTimeLeft] = useState<number>(0)
  const { toast } = useToast()

  // Gerar QR Code a partir do texto
  useEffect(() => {
    if (pixData.pix.qr_code_text) {
      QRCode.toDataURL(pixData.pix.qr_code_text, {
        width: 300,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#ffffff',
        },
      })
        .then((url) => setQrCodeDataUrl(url))
        .catch((err) => console.error('Erro ao gerar QR Code:', err))
    }
  }, [pixData.pix.qr_code_text])

  // Countdown timer
  useEffect(() => {
    const expiresAt = new Date(pixData.pix.expires_at).getTime()
    const now = Date.now()
    const diffInSeconds = Math.floor((expiresAt - now) / 1000)

    setTimeLeft(diffInSeconds > 0 ? diffInSeconds : 0)

    const interval = setInterval(() => {
      const newExpiresAt = new Date(pixData.pix.expires_at).getTime()
      const newNow = Date.now()
      const newDiff = Math.floor((newExpiresAt - newNow) / 1000)

      if (newDiff <= 0) {
        setTimeLeft(0)
        clearInterval(interval)
      } else {
        setTimeLeft(newDiff)
      }
    }, 1000)

    return () => clearInterval(interval)
  }, [pixData.pix.expires_at])

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(pixData.pix.qr_code_text)
      setCopied(true)
      toast({
        title: 'Código copiado!',
        description: 'Cole no seu aplicativo de pagamentos',
      })

      setTimeout(() => setCopied(false), 3000)
    } catch (err) {
      toast({
        title: 'Erro ao copiar',
        description: 'Tente novamente',
        variant: 'destructive',
      })
    }
  }

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`
  }

  const isExpired = timeLeft <= 0

  return (
    <Card>
      <CardHeader className="text-center">
        <CardTitle className="flex items-center justify-center gap-2">
          <QrCodeIcon className="h-6 w-6" />
          Pagamento via PIX
        </CardTitle>
        <CardDescription>
          Escaneie o QR Code ou copie o código abaixo
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Valor */}
        <div className="text-center">
          <p className="text-sm text-muted-foreground">Valor a pagar</p>
          <p className="text-3xl font-bold text-primary">
            {formatCurrency(pixData.amount)}
          </p>
        </div>

        <Separator />

        {/* Timer */}
        {!isExpired ? (
          <div className="flex items-center justify-center gap-2 rounded-lg bg-muted p-3">
            <Clock className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm">
              Código expira em: <strong className="text-primary">{formatTime(timeLeft)}</strong>
            </span>
          </div>
        ) : (
          <div className="rounded-lg bg-destructive/10 p-3 text-center">
            <p className="text-sm font-medium text-destructive">
              Código PIX expirado. Recarregue a página para gerar um novo.
            </p>
          </div>
        )}

        {/* QR Code */}
        {qrCodeDataUrl && !isExpired && (
          <div className="flex justify-center">
            <div className="rounded-lg border-4 border-primary/20 bg-white p-4">
              <img
                src={qrCodeDataUrl}
                alt="QR Code PIX"
                className="h-[300px] w-[300px]"
              />
            </div>
          </div>
        )}

        {/* Código Copia e Cola */}
        {!isExpired && (
          <div className="space-y-2">
            <label className="text-sm font-medium">
              Ou copie o código PIX:
            </label>
            <div className="flex gap-2">
              <div className="flex-1 rounded-md border bg-muted px-3 py-2">
                <p className="truncate text-xs font-mono">
                  {pixData.pix.qr_code_text}
                </p>
              </div>
              <Button
                onClick={handleCopy}
                variant="outline"
                size="icon"
                className="shrink-0"
              >
                {copied ? (
                  <Check className="h-4 w-4 text-green-600" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Instruções */}
        <div className="space-y-2 rounded-lg bg-muted p-4">
          <h4 className="font-semibold text-sm">Como pagar:</h4>
          <ol className="list-decimal list-inside space-y-1 text-sm text-muted-foreground">
            <li>Abra o app do seu banco</li>
            <li>Escolha pagar via PIX</li>
            <li>Escaneie o QR Code ou cole o código</li>
            <li>Confirme o pagamento</li>
          </ol>
        </div>

        {/* Status */}
        <div className="text-center">
          <div className="inline-flex items-center gap-2 rounded-full bg-yellow-100 px-4 py-2 text-sm text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400">
            <div className="h-2 w-2 animate-pulse rounded-full bg-yellow-600" />
            Aguardando pagamento...
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
